/**
 * コンテキストヘルプ機能
 * FloatingWindow のタイトルバーに「?」アイコンを表示し、
 * クリックでその機能専用のAIヘルプを開く
 *
 * 撤収: CONTEXT_HELP_ENABLED を false にすれば完全無効化
 */

// フィーチャーフラグ（false で完全無効化）
export const CONTEXT_HELP_ENABLED = true

/**
 * FloatingWindow の windowId → ガイドページ情報のマッピング
 * ここに登録されている windowId のみ「?」アイコンが表示される
 */
export const contextHelpMap = {
  // ドロー系
  'draw-share-window': { title: 'マイドロー', page: 'shared-draw.html' },
  'draw': { title: 'ドロー', page: 'draw.html' },

  // ジオリファレンス
  'warp-wizard': { title: 'ジオリファレンス', page: 'georef.html' },

  // ツールボックス系
  'elevation-dialog': { title: '断面図', page: 'elevation-profile.html' },
  'watershed-window': { title: '流域計算', page: 'watershed.html' },
  'map-compare-window': { title: 'AI地図チャット', page: 'ai-chat.html' },
  'ai-osm-search-window': { title: 'AI OSM検索', page: 'ai-osm.html' },
  'time-slider-window': { title: 'スワイプ比較', page: 'swipe.html' },

  // レイヤー・マイルーム系（動的ID対応はプレフィックスマッチで）
  // layer-window-map1, layer-window-map2 等
  'layer-window': { title: 'レイヤー', page: 'layers.html' },
  // 'myroom-window': マイルームはリロードボタンに置換済み
  'share-window': { title: '共有', page: 'share.html' },
  'menu-window': { title: '基本設定', page: 'settings.html' },

  // AI系レポート
  'property-report': { title: 'AI物件調査', page: 'ai-bukken.html' },
  'childcare-report': { title: 'AI子育てレポート', page: 'ai-kosodate.html' },
  'super-population': { title: 'AI人口プロファイル', page: 'ai-jinko.html' },
  'area-analysis': { title: 'AI防災リスク診断', page: 'ai-bousai-risk.html' },

  // 筆界・測量系
  'kyuseki-import': { title: '求積表インポート', page: 'kyuseki.html' },
  'chiban-info-window': { title: '地番図', page: 'chibanzu.html' },
  'chibanzu-info-window': { title: '地番図', page: 'chibanzu.html' },

  // 画面中心情報
  'zoom-info': { title: '画面中心情報', page: 'zoom-info.html' },

  // 測量系
  'job-picker': { title: 'ジョブリスト', page: 'sokui.html' },

  // グループ
  'group-manage-window': { title: 'グループチャット', page: 'group-chat.html' },

  // ヘルプ（自身は除外）
  // 'ai-help-chat' は除外
}

/**
 * ext ダイアログ（dialog-ext-{mapName}-{itemId}）用マッピング
 * itemId のプレフィックスで対応ガイドページを振り分ける
 * 順番に評価されるため、長いプレフィックスを先に書く
 */
const extHelpRules = [
  // DEM系
  { prefix: 'oh-dem-', page: 'ext-dem.html', title: '標高・地形解析' },
  { prefix: 'oh-sea-level', page: 'ext-dem.html', title: '海面上昇シミュレーション' },
  // 気象系
  { prefix: 'oh-rain-radar', page: 'ext-weather.html', title: '雨雲レーダー' },
  { prefix: 'tenki', page: 'ext-weather.html', title: '雨雲レーダー' },
  { prefix: 'oh-himawari', page: 'ext-weather.html', title: 'ひまわり衛星' },
  { prefix: 'oh-kikukuru', page: 'ext-weather.html', title: 'キキクル' },
  // 交通系
  { prefix: 'oh-highway', page: 'ext-transport.html', title: '高速道路時系列' },
  { prefix: 'oh-tetsudojikeiretsu', page: 'ext-transport.html', title: '鉄道時系列' },
  { prefix: 'oh-bus', page: 'ext-transport.html', title: 'バスルート' },
  { prefix: 'oh-ekibetsukyaku', page: 'ext-transport.html', title: '駅別乗降客数' },
  // 境界・行政系
  { prefix: 'oh-syochiiki', page: 'ext-boundary.html', title: '小地域・町丁字' },
  { prefix: 'oh-city-', page: 'ext-boundary.html', title: '市町村境界' },
  { prefix: 'oh-chibanzu', page: 'ext-boundary.html', title: '地番図' },
  { prefix: 'citychibanzu', page: 'ext-boundary.html', title: '地番図' },
  { prefix: 'oh-bakumatsu', page: 'ext-boundary.html', title: '幕末期近世の村' },
  { prefix: 'oh-koaza', page: 'ext-boundary.html', title: '関東小字地図' },
  { prefix: 'oh-geopark', page: 'ext-boundary.html', title: 'ジオパーク' },
  { prefix: 'oh-gyosei-history', page: 'ext-boundary.html', title: '行政界履歴' },
  { prefix: 'city', page: 'ext-boundary.html', title: '行政界' },
  // ベースマップ系
  { prefix: 'oh-vector-', page: 'ext-basemap.html', title: 'ベクタースタイル' },
  { prefix: 'oh-mpi-rrim', page: 'ext-basemap.html', title: '赤色立体地図' },
  { prefix: 'oh-gsi-', page: 'ext-basemap.html', title: '地理院タイル' },
  // その他
  { prefix: 'oh-tadankai', page: 'ext-misc.html', title: '多段階浸水想定' },
  { prefix: 'oh-kozui', page: 'ext-misc.html', title: '洪水浸水想定' },
  { prefix: 'oh-densho', page: 'ext-misc.html', title: '自然災害伝承碑' },
  { prefix: 'oh-sekibutsu', page: 'ext-misc.html', title: '石仏調査' },
  { prefix: 'oh-nrct-', page: 'ext-misc.html', title: '日本歴史地名大系' },
  { prefix: 'oh-gsj-', page: 'ext-misc.html', title: '地質図' },
  { prefix: 'oh-price-heatmap', page: 'ext-misc.html', title: '不動産価格' },
  { prefix: 'tokei', page: 'ext-misc.html', title: '不動産価格' },
  // 基準点系
  { prefix: 'oh-homusyo-', page: 'ext-misc.html', title: '登記所地図' },
  { prefix: 'oh-kizyunten', page: 'ext-misc.html', title: '基本基準点' },
  { prefix: 'oh-toshikan', page: 'ext-misc.html', title: '都市部基準点' },
  { prefix: 'oh-gaiku', page: 'ext-misc.html', title: '街区基準点' },
  { prefix: 'oh-minden', page: 'ext-misc.html', title: '電子基準点' },
  { prefix: 'oh-ntrip', page: 'ext-misc.html', title: 'RTK基準局' },
  { prefix: 'oh-zeni', page: 'ext-misc.html', title: '善意の基準局' },
  { prefix: 'oh-saga-', page: 'ext-misc.html', title: '佐賀県基準点' },
  // 航空写真系
  { prefix: 'oh-sp', page: 'ext-misc.html', title: '航空写真' },
  { prefix: 'oh-jinsoku', page: 'ext-misc.html', title: '迅速測図' },
]

/**
 * windowId からコンテキストヘルプ情報を取得
 * 動的ID（xxx-map1 等）はプレフィックスマッチで対応
 * @param {string} windowId
 * @returns {{ title: string, page: string } | null}
 */
export function getContextHelp (windowId) {
  if (!CONTEXT_HELP_ENABLED) return null

  // 完全一致
  if (contextHelpMap[windowId]) return contextHelpMap[windowId]

  // プレフィックスマッチ（layer-window-map1 → layer-window）
  for (const key of Object.keys(contextHelpMap)) {
    if (windowId.startsWith(key + '-')) return contextHelpMap[key]
  }

  // ext ダイアログ（dialog-ext-{mapName}-{itemId}）の振り分け
  // 一時無効化（将来復活の可能性あり）
  // const extMatch = windowId.match(/^dialog-ext-[^-]+-(.+)$/)
  // if (extMatch) {
  //   const itemId = extMatch[1]
  //   for (const rule of extHelpRules) {
  //     if (itemId.startsWith(rule.prefix) || itemId === rule.prefix) {
  //       return { title: rule.title, page: rule.page }
  //     }
  //   }
  //   // ルールに一致しないext → 汎用ガイド
  //   return { title: 'レイヤー設定', page: 'layers.html' }
  // }

  return null
}
